/**
 * PostHog event catalog (PLA2-366 / Sys9) — mirrors ANALYTICS_SPEC.md §2.
 *
 * Event names are snake_case and stable: PostHog funnels/insights reference them
 * by exact string, so don't rename without updating the dashboards in the spec.
 */

export const ANALYTICS_EVENTS = {
  // Session
  SESSION_STARTED: "session_started",
  PRO_LOGOUT: "pro_logout",

  // Marketing / acquisition (PLA2-339 — DMA / use-case landing pages)
  LANDING_PAGE_VIEW: "landing_page_view",

  // Design flow funnel
  DESIGN_STARTED: "design_started",
  PHOTO_UPLOADED: "photo_uploaded",
  // PLA2-822: automatic "how much of this photo is yard" check after upload.
  PHOTO_CHECKED: "photo_checked",
  MASK_DRAWN: "mask_drawn",
  ZIPCODE_ENTERED: "zipcode_entered",
  // PLA2-688: retailer can't ship to the ZIP's state → redirect screen.
  SHIP_RESTRICTED_SHOWN: "ship_restricted_shown",
  SHIP_RESTRICTED_PLANTISTA_CLICKED: "ship_restricted_plantista_clicked",
  SHIP_RESTRICTED_BACK_CLICKED: "ship_restricted_back_clicked",
  PLANTS_SELECTED: "plants_selected",
  GENERATION_STARTED: "generation_started",
  // NOTE: generation_succeeded and generation_failed are fired SERVER-SIDE
  // (api/routes/generate.py and worker/main.py via api/services/analytics.py) so
  // elapsed_ms is the real generation time and failures are actually recorded.
  // These constants remain for the read-side (funnel-query.ts). Do not capture
  // them from the client — it would double-count.
  GENERATION_SUCCEEDED: "generation_succeeded",
  GENERATION_FAILED: "generation_failed",
  DESIGN_COMPLETED: "design_completed",

  // PLA2-947: full-page outage takeover on /design + its waitlist form.
  DESIGNER_OUTAGE_VIEWED: "designer_outage_viewed",
  DESIGNER_WAITLIST_SUBMITTED: "designer_waitlist_submitted",
  DESIGNER_OUTAGE_WEBSITE_CLICKED: "designer_outage_website_clicked",

  // Recipe & plant usage
  RECIPE_SELECTED: "recipe_selected",
  RECIPE_CHANGED: "recipe_changed",
  PLANT_ADDED: "plant_added",
  PLANT_REMOVED: "plant_removed",

  // Out-of-stock alternatives (substitutes for sold-out recipe plants)
  SUBSTITUTES_SHOWN: "substitutes_shown",
  SUBSTITUTE_ACCEPTED: "substitute_accepted",
  RECIPE_AUTO_HEALED: "recipe_auto_healed",

  // Container-garden flow (CONTAINER_GARDEN)
  // PLA2-973 funnel: garden_type_selected → planter_selected → recipe_selected
  // → container_design_generated → design_email_sent → cart_add →
  // checkout_completed. Every event after entry carries the `garden_type`
  // super property, and orders carry `flow: "container"` (see
  // ANALYTICS_SPEC.md "Container-garden funnel").
  GARDEN_TYPE_SELECTED: "garden_type_selected",
  PLANTER_SELECTED: "planter_selected",
  // "Buy this planter" — outbound click to the planter's own store page
  // (Home Depot, Dirt Locker…). We never see that sale, so clicks per planter
  // are the proxy.
  PLANTER_BUY_CLICKED: "planter_buy_clicked",
  CONTAINER_DESIGN_GENERATED: "container_design_generated",
  CONTAINER_DESIGN_FAILED: "container_design_failed",
  CONTAINER_PDF_DOWNLOADED: "container_pdf_downloaded",

  // Sharing & email
  DESIGN_EMAIL_SENT: "design_email_sent",
  DESIGN_EMAIL_FAILED: "design_email_failed",

  // Cart & checkout
  CART_ADD: "cart_add",
  CART_REMOVE: "cart_remove",
  CHECKOUT_STARTED: "checkout_started",
  // Fired SERVER-SIDE from both provider webhooks — app/api/webhooks/shopify
  // (route.ts, `orders/paid`) and lib/commerce/stripe-webhook.ts
  // (`checkout.session.completed`) — never from the client (PLA2-366 §5).
  // PLA2-541/542-547 properties, kept identical across both paths:
  //   designer_sourced: bool  — true iff the order carries a Designer
  //     project_id (cart attribute / session metadata). Events captured
  //     before this property existed have no such key at all; readers
  //     (lib/analytics/commerce-query.ts) fall back to `project_id`
  //     presence for those.
  //   recipe_id: string|null  — the design's recipe/style, when the cart
  //     carried one ("top purchased recipes").
  //   customer_type: "new"|"returning" — set only on the Shopify path, only
  //     when the webhook payload's `customer.orders_count` is present.
  //     Never set on the Stripe path (no reliable signal without an extra
  //     API call). Readers fall back to a hashed-email "seen before"
  //     heuristic for events with no customer_type.
  CHECKOUT_COMPLETED: "checkout_completed",

  // Pro / landscaper flow (ANALYTICS_SPEC §2 "Pro-Specific Events")
  PRO_PROJECT_CREATED: "pro_project_created",
  PRO_PROJECT_OPENED: "pro_project_opened",
  PRO_DESIGN_SAVED: "pro_design_saved",
  PRO_ANNOTATION_ADDED: "pro_annotation_added",
  PRO_NOTE_ADDED: "pro_note_added",
  VIBE_TRANSFER_USED: "vibe_transfer_used",
} as const;

export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type AccountType = "pro" | "gc" | "d2c";

/** Property bag — snake_case keys per the spec. Never include raw email/zip/PII. */
export type AnalyticsProps = Record<
  string,
  string | number | boolean | null | undefined | string[]
>;
